import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { clearFormEdited, insertElement } from "./editorSlice";

export type ToolbarActionMode =
  | "select"
  | "insert-field"
  | "insert-text"
  | "insert-image"
  | "insert-layout"
  | "insert-table";

export const DEFAULT_ACTION_MODE: ToolbarActionMode = "select";

type ToolbarState = {
  showFullscreen: boolean;
  showKeyboardActions: boolean;
  editorMode: {
    isLive: boolean;
    mode: ToolbarActionMode;
    fieldTypeIdMode: number | null;
  };
};

const initialState: ToolbarState = {
  showFullscreen: false,
  showKeyboardActions: false,
  editorMode: {
    isLive: false,
    mode: DEFAULT_ACTION_MODE,
    fieldTypeIdMode: null,
  },
};

export const toolbarSlice = createSlice({
  name: "developer/edit/toolbar",
  initialState,
  reducers: {
    displayFullscreen(state, action: PayloadAction<boolean | undefined>) {
      if (action.payload) state.showFullscreen = action.payload;
      else state.showFullscreen = !state.showFullscreen;
    },
    toggleLiveMode(state, action: PayloadAction<boolean>) {
      if (state.editorMode.mode.startsWith("insert") && action.payload) {
        state.editorMode.mode = DEFAULT_ACTION_MODE;
      }
      state.editorMode.isLive = action.payload;
    },
    toggleModeEditor(state, action: PayloadAction<ToolbarActionMode>) {
      const newMode = action.payload;
      if (newMode.startsWith("insert") && state.editorMode.isLive) {
        state.editorMode.isLive = false;
      }
      state.editorMode.mode = newMode;

      if (state.showKeyboardActions) {
        state.showKeyboardActions = false;
      }
    },
    onChangeFieldTypeIdMode: (
      state,
      action: PayloadAction<number | null>
    ): void => {
      state.editorMode.fieldTypeIdMode = action.payload;
      state.editorMode.mode = "insert-field";
      state.editorMode.isLive = false;
    },
    onToggleShowKeyboardActions: (
      state,
      action: PayloadAction<boolean>
    ): void => {
      state.showKeyboardActions = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(insertElement.type, (state) => {
        state.editorMode.mode = DEFAULT_ACTION_MODE;
      })
      .addCase(clearFormEdited.type, () => initialState);
  },
});

export const {
  displayFullscreen,
  toggleLiveMode,
  toggleModeEditor,
  onChangeFieldTypeIdMode,
  onToggleShowKeyboardActions,
} = toolbarSlice.actions;
